import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { analyticsActions } from '@actions';
import { subtractYears } from '@helpers/dates';
import { ProfileName } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { getPresenceOptions } from '@helpers/analytics';
import { useLocations } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import PresenceRates from './PresenceRates';
import SearchKeywordsTable from './SearchKeywordsTable';
import DevicesBreakdown from './DevicesBreakdown';
import PresenceTable from './PresenceTable';
import PresenceViewsTrend from './PresenceViewsTrend';
import PresenceEngagementsTrend from './PresenceEngagementsTrend';

const Wrapper = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    marginBottom: 16,
    '@media (max-width: 1366px)': {
        gridTemplateColumns: '100%',
    },
});

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const Presence = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        platforms: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { presenceData, presenceLoading } = useSelector(state => state.analytics);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();

    const availableLocations = useMemo(
        () =>
            locations.filter(l =>
                l.profiles?.some(p => p.type === ProfileName.GoogleOfficial || p.type === ProfileName.FacebookOfficial),
            ),
        [locations],
    );

    const locationsConnectedIds = useMemo(() => availableLocations.map(l => l.companyId), [availableLocations]);
    const { platformsConnectedIds } = useMemo(
        () => getPresenceOptions(locations, locationsConnectedIds),
        [locations, locationsConnectedIds],
    );

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            locations: mapLocationsToFormValue(availableLocations),
            platforms: platformsConnectedIds,
        }));
    }, [availableLocations, dispatch]);

    useEffect(() => {
        if (filter.locations.length) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsPresence({
                locations: filter.locations.map(item => item.id),
                platforms: filter.platforms,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
            }),
        );
    };

    return (
        <StyledAnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    locations={availableLocations}
                    filter={filter}
                    setFilter={setFilter}
                    type="presence"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                    withPresence
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-presence">
                <PresenceRates data={presenceData.rates} />
                <PresenceViewsTrend data={presenceData.viewsDistribution} language={language} />
                <PresenceEngagementsTrend data={presenceData.engagementsDistribution} language={language} />
                <Wrapper>
                    <DevicesBreakdown
                        total={presenceData.rates.views}
                        data={presenceData.devicesDistribution}
                        isLoading={presenceLoading}
                    />
                    <SearchKeywordsTable data={presenceData.keywords} isLoading={presenceLoading} />
                </Wrapper>
                <PresenceTable
                    data={presenceData.locationsDistribution}
                    isLoading={presenceLoading}
                    locations={availableLocations}
                    filter={filter}
                />
            </AnalyticsContainer>
        </StyledAnalyticsWrapper>
    );
});

Presence.displayName = 'Presence';

export default Presence;
